import React, { useEffect, useState } from 'react';
import { Form } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { type FieldError, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { handleRegister, reset } from './register.reducer';
import { toast } from 'react-toastify';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';

export const RegisterPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const successMessage = useAppSelector(state => state.register.successMessage);

  useEffect(
    () => () => {
      dispatch(reset());
    },
    [],
  );
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      navigate('/login');
    }
  }, [successMessage]);

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, touchedFields },
  } = useForm({ mode: 'onTouched' });

  const handleValidSubmit = ({ username, email, firstPassword, secondPassword }) => {
    if (firstPassword !== secondPassword) {
      toast.error(t('register.passwordMismatch') || 'Passwords do not match');
      return;
    }
    dispatch(handleRegister({ login: username, email, password: firstPassword, langKey: 'en' }));
  };

  const strength = pw.length < 4 ? 1 : pw.length < 8 ? 3 : 5;
  const sColors = [colors.danger, colors.danger, colors.accent, colors.accent, colors.success, colors.success];

  const InputField = ({ name, label, type = 'text', placeholder, validation, onChange = undefined }) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: colors.muted, marginBottom: 6 }}>{label}</label>
      <input
        className="ms-input"
        type={type}
        placeholder={placeholder}
        {...register(name, validation)}
        onChange={e => {
          register(name).onChange(e);
          if (onChange) onChange(e);
        }}
      />
      {errors[name] && <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>{(errors[name] as FieldError).message}</div>}
    </div>
  );

  return (
    <div className="ms-auth">
      <div className="ms-auth__card ms-card">
        <div className="ms-auth__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                fontWeight: 800,
                color: '#000',
              }}
            >
              M
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>
              Q8<span style={{ color: colors.accent }}>Tools</span>
            </span>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: '8px 0 0' }}>{t('register.title')}</h2>
        </div>
        <div className="ms-auth__body">
          <Form onSubmit={handleSubmit(handleValidSubmit)}>
            <InputField
              name="username"
              label={t('register.usernameLabel')}
              placeholder={t('register.usernamePlaceholder')}
              validation={{
                required: t('register.usernameRequired'),
                minLength: { value: 1, message: t('register.minLength1') },
                maxLength: { value: 50, message: t('register.maxLength50') },
              }}
            />
            <InputField
              name="email"
              label="Email"
              type="email"
              placeholder="email@example.com"
              validation={{ required: t('register.emailRequired') }}
            />
            <InputField
              name="firstPassword"
              label={t('register.passwordLabel')}
              type="password"
              placeholder={t('register.minLength4')}
              validation={{ required: t('register.passwordRequired'), minLength: { value: 4, message: t('register.minLength4') } }}
              onChange={e => setPw(e.target.value)}
            />
            <div className="ms-pw-strength">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="ms-pw-strength__bar" style={{ background: i < strength ? sColors[strength] : colors.surface }} />
              ))}
            </div>
            <InputField
              name="secondPassword"
              label={t('register.confirmLabel')}
              type="password"
              placeholder={t('register.confirmPlaceholder')}
              validation={{ required: t('register.confirmRequired') }}
            />
            <button className="ms-btn ms-btn--primary" type="submit" style={{ width: '100%', padding: 12 }}>
              {t('register.submit')}
            </button>
          </Form>
        </div>
        <div className="ms-auth__footer">
          {t('register.hasAccount')}{' '}
          <Link to="/login" style={{ fontWeight: 600 }}>
            {t('register.loginLink')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
